import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import FurtherReading from '../FurtherReading';
import { DEFAULT_ROLE_READING } from '../blogPosts';
import RoleContactModal from './RoleContactModal';
import RoleLandingPageV2 from './RoleLandingPageV2';
import { ROLE_V2 } from './v2';
import {
  ROLE_CONFIGS,
  ROLE_SLUGS,
  SHARED_TRAITS,
  SHARED_TEAMS_FAQ,
} from './roleConfigs';
import '../AIEmployeesLandingPage.css';
import './RoleLandingPage.css';

/**
 * RoleLandingPage — ONE shared template for every programmatic-SEO role page
 * at /ai-employees/ai-{role-slug}. Driven entirely by roleConfigs.js; renders
 * on the .aie-landing design system (monochrome-first, Linear/Apple-grade,
 * teal as rare spotlight) inside MarketingRoute.
 *
 * MOTION DESIGN (restraint + one signature moment per page):
 *  - Choreographed hero entrance (pill → title → sub → CTAs).
 *  - Staggered card reveals via CSS animation (rolp-stagger), premium easing.
 *  - Subtle parallax on the hero glow.
 *  ("The math" salary-vs-price stat beat, its count-up, the Cost table row and
 *  the price footnote were removed Sep 6 2026, Nitish ruling: no cost figure or
 *  human-salary comparison on role pages; the cost FAQ carries the shared answer.)
 *  All progressive enhancement: content is fully in the DOM for crawlers, and
 *  every effect is disabled under prefers-reduced-motion. No scroll-jacking.
 *
 * Theming follows AIEmployeesLandingPage exactly: dark-only in the logged-out
 * marketing shell; honors app theme when logged in.
 *
 * CTAs (PRD D2): primary → adaptive signup/hire; secondary "Talk to us" →
 * lead modal (fires generate_lead). Unknown slug → redirect /ai-employees.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** True while react-snap's puppeteer is prerendering (it sets a ReactSnap
 *  user agent). Used to freeze animations so the crawled HTML carries the
 *  exact final values (same prerender-stability trick as RoleRotator). */
const IS_PRERENDER = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');


const Section = ({ className = '', label, title, sub, children, narrow }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section rolp-section ${narrow ? 'rolp-section--narrow' : ''} ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head rolp-section-head">
            {label && <span className="rolp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
            {sub && <p className="aie-section-sub">{sub}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};


const RoleLandingPage = () => {
  const { roleSlug } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const [modalOpen, setModalOpen] = useState(false);
  const glowRef = useRef(null);

  // Stash the visited role for the hire page's SEO-role prefill (last-touch wins: whichever
  // role page the visitor saw most recently). Captured at page VISIT, not CTA click, so visitors
  // who convert via header nav / direct /signup still carry their role intent. Skipped during
  // prerender (react-snap must not bake a stash into the snapshot). Consumed + cleared by
  // NewEmployeePage. See agent_docs/hire_chips_preexpand_and_seo_role_prefill_prd.md Part B.
  useEffect(() => {
    const cfg = ROLE_CONFIGS[roleSlug];
    if (!cfg || IS_PRERENDER) return;
    try {
      sessionStorage.setItem(
        'cellcog_seo_role',
        JSON.stringify({ slug: cfg.slug, roleDisplay: cfg.roleDisplay, article: cfg.article })
      );
    } catch (e) { /* storage unavailable — prefill is a nice-to-have */ }
  }, [roleSlug]);

  // Subtle parallax on the hero glow (rAF-throttled, reduced-motion aware).
  useEffect(() => {
    if (window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (glowRef.current) {
          glowRef.current.style.transform = `translate3d(0, ${window.scrollY * 0.18}px, 0)`;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const config = ROLE_CONFIGS[roleSlug];
  if (!config) return <Navigate to="/ai-employees" replace />;

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/ai-employees/${config.slug}`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/ai-employees/${config.slug}-og.jpg`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', role: config.roleDisplay, location: loc });
    if (!currentUser) {
      // Post-auth, land the visitor DIRECTLY on the hire page (with the role prefill from the
      // sessionStorage stash above) instead of back on this marketing page. This is the real
      // post-auth routing mechanism (persistentNavigation sessionStorage); the old `?return=`
      // query param was dead code — nothing ever read it.
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const handleTalk = (loc) => {
    trackEvent('contact_cta_click', { role: config.roleDisplay, cta_location: loc });
    setModalOpen(true);
  };

  // Role-page experiment (Sep 23 2026): wave-1 slugs render the v2 layout.
  const v2 = ROLE_V2[config.slug];
  if (v2) {
    return (
      <>
        <RoleLandingPageV2
          content={v2}
          config={config}
          dark={dark}
          glowRef={glowRef}
          onHire={handleHire}
          onTalk={handleTalk}
        />
        <RoleContactModal open={modalOpen} onClose={() => setModalOpen(false)} roleDisplay={config.roleDisplay} dark={dark} />
      </>
    );
  }

  // Role-specific FAQs + the shared teams FAQ (positioning canon, Jul 2026).
  const faq = [...config.faq, SHARED_TEAMS_FAQ];

  const relatedSlugs = ROLE_SLUGS.filter((s) => s !== config.slug);

  return (
    <>
      <Helmet>
        <title>{config.seo.title}</title>
        <meta name="description" content={config.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={config.seo.title} />
        <meta property="og:description" content={config.seo.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={`${config.roleDisplay} illustration`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.seo.title} />
        <meta name="twitter:description" content={config.seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateFAQSchema(faq.map((f) => ({ question: f.q, answer: f.a })))} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: `${SEO_DEFAULTS.url}/` },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: config.roleDisplay, url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing rolp ${dark ? 'dark' : ''}`}>
        {/* Hero — dark cinematic band with entrance choreography + parallax glow */}
        <section className="rolp-hero">
          <div className="rolp-hero__glow" aria-hidden="true" ref={glowRef} />
          <div className="rolp-hero__inner">
            {/* Crawlable up-link matching the BreadcrumbList schema: gives
                bots (and users) a real anchor from every role page back to
                the /ai-employees hub. */}
            <nav className="rolp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>{config.roleDisplay}</span>
            </nav>
            <span className="rolp-hero__pill">
              <span className="rolp-hero__pill-dot" aria-hidden="true" />
              CellCog AI Employee
            </span>
            <h1 className="rolp-hero__title aie-display">
              {config.roleDisplay}
              <br />
              <span className="rolp-hero__title-accent">hired in minutes, works every day.</span>
            </h1>
            <p className="rolp-hero__sub">{config.hook}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => handleTalk('hero')}>
                Talk to us
              </button>
            </div>
          </div>
        </section>


        {/* Capabilities — the role-specific day-to-day */}
        <Section
          label="Capabilities"
          title={`What they do as your ${config.roleDisplay}`}
          sub="The work they take on, every day."
        >
          <div className="rolp-cap-grid rolp-stagger">
            {config.capabilities.map((c, i) => (
              <div className="rolp-cap-card" key={c.title}>
                <span className="rolp-cap-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Shared traits — compact strip (identical across roles by design) */}
        <Section label="Every CellCog AI Employee" title="More than a tool, a real AI employee">
          <div className="rolp-trait-strip rolp-stagger">
            {SHARED_TRAITS.map((t) => (
              <div className="rolp-trait" key={t.title}>
                <CheckCircleOutlined className="rolp-trait__icon" />
                <span><b>{t.title}</b> {t.body}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Prompt library */}
        <Section
          label="Prompt library"
          title="Prompts that return real deliverables"
          sub="Copy, paste, and adapt. Each one comes back as a finished artifact."
          narrow
        >
          <div className="rolp-prompts rolp-stagger">
            {config.prompts.map((p, i) => (
              <div className="rolp-prompt" key={p.q}>
                <span className="rolp-prompt__num">{i + 1}</span>
                <div>
                  <p className="rolp-prompt__q">&ldquo;{p.q}&rdquo;</p>
                  <p className="rolp-prompt__a">Delivers: <b>{p.deliverable}</b></p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Use cases */}
        <Section label="Use cases" title={`How teams use them for ${config.roleNoun} work`}>
          <div className="rolp-uc-grid rolp-stagger">
            {config.useCases.map((u) => (
              <div className="rolp-uc-card" key={u.badge}>
                <span className="rolp-uc-badge">{u.badge}</span>
                <h3>{u.title}</h3>
                <p>{u.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Honest limits — the trust beat, deliberately quiet */}
        <Section
          label="Honest limits"
          title="What stays with you"
          sub="The calls that should stay yours."
          narrow
        >
          <div className="rolp-limits">
            {config.limits.map((l) => (
              <div className="rolp-limit" key={l.title}>
                <b>{l.title}</b>
                <p>{l.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Comparison. Oct 14 2026 (Nitish): no human-hire comparison; roles with comparison3 show three ways to get
            the job done (yourself / the tool you would use / the AI employee), the same shape as the v2 pages. */}
        {config.comparison3 ? (
          <Section label="Compare" title={config.comparison3.title} narrow>
            <div className="rolp-cmp-wrap">
              <table className="rolp-cmp">
                <thead>
                  <tr>
                    <th aria-label="Dimension" />
                    {config.comparison3.cols.map((col, i) => (
                      <th key={col} className={i === config.comparison3.cols.length - 1 ? 'rolp-cmp__ai' : undefined}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {config.comparison3.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      {row.cells.map((cell, i) => (
                        <td key={i} className={i === row.cells.length - 1 ? 'rolp-cmp__ai' : undefined}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        ) : null}

        {/* FAQ */}
        <Section label="FAQ" title="Frequently asked questions" narrow>
          <div className="rolp-faq">
            {faq.map((f) => (
              <details className="rolp-faq__item" key={f.q}>
                <summary>{f.q}</summary>
                <p>
                  {f.a}
                  {f.link && (
                    <>
                      {' '}
                      <Link className="rolp-faq__link" to={f.link.href}>
                        {f.link.label}
                      </Link>
                    </>
                  )}
                </p>
              </details>
            ))}
          </div>
        </Section>

        {/* Related roles */}
        {relatedSlugs.length > 0 && (
          <Section label="Related roles" title="Other roles you can hire for">
            {/* Real <Link> anchors (NOT buttons) so crawlers can follow the
                role cluster: this is the sibling-linking surface. */}
            <div className="rolp-rel-grid rolp-stagger">
              {relatedSlugs.map((s) => (
                <Link className="rolp-rel-card" key={s} to={`/ai-employees/${s}`}>
                  <span>{ROLE_CONFIGS[s].roleDisplay}</span>
                  <ArrowRightOutlined />
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Further reading — internal links into the blog cluster (2026-07-30).
            Default: the three category-basics posts (relevant to hiring any
            role); a config can override with its own relatedPosts list. */}
        <Section label="Further reading" title="From the blog">
          <FurtherReading slugs={config.relatedPosts || DEFAULT_ROLE_READING} />
        </Section>

        {/* Close band */}
        <section className="rolp-close">
          <div className="rolp-close__glow" aria-hidden="true" />
          <div className="rolp-close__inner">
            <h2 className="aie-display">{config.ctaBand.title}</h2>
            <p>{config.ctaBand.body}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('close')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => handleTalk('close')}>
                Talk to us
              </button>
            </div>
          </div>
        </section>
      </div>

      <RoleContactModal open={modalOpen} onClose={() => setModalOpen(false)} roleDisplay={config.roleDisplay} dark={dark} />
    </>
  );
};

export default RoleLandingPage;
